import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Trash2, Undo2, ImagePlus, Loader2 } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const IMG = /\.(jpe?g|png|webp|gif|bmp|tiff?)$/i;

const Tile = ({ p, n, removed, onDrop, onToggle, dragId }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      draggable={!removed}
      onDragStart={(e) => { dragId.current = p.index; e.dataTransfer.effectAllowed = "move"; }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(p.index); }}
      className={`group relative cursor-grab rounded-xl p-1.5 transition-all active:cursor-grabbing ${over ? "bg-[#3D3DF0]/10 ring-2 ring-[#3D3DF0]" : ""} ${removed ? "opacity-35" : ""}`}
      data-testid={`organize-page-${p.index}`}
    >
      <div className="relative overflow-hidden rounded-lg bg-white shadow-[6px_6px_12px_rgba(166,180,208,.6)]" style={{ aspectRatio: `${p.width}/${p.height}` }}>
        <img src={fileUrl(p.thumb)} alt={`Page ${p.index}`} className="h-full w-full object-cover" draggable={false} />
        <GripVertical className="absolute left-1 top-1 h-4 w-4 rounded bg-white/80 text-[#64748B] opacity-0 group-hover:opacity-100" />
        <button onClick={() => onToggle(p.index)} aria-label={removed ? "Restore page" : "Delete page"} data-testid={`organize-remove-${p.index}`}
          className={`absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full text-white shadow ${removed ? "bg-[#3D3DF0]" : "bg-[#A62B8A] opacity-0 group-hover:opacity-100"}`}>
          {removed ? <Undo2 className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </div>
      <p className="mt-1 text-center font-mono text-[10px] text-[#64748B]">{removed ? "deleted" : n}{!removed && n !== p.index ? ` · was ${p.index}` : ""}</p>
    </div>
  );
};

const AddPages = ({ count, busy, onAdd }) => {
  const [pos, setPos] = useState(String(count));
  const input = useRef();
  useEffect(() => setPos(String(count)), [count]);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl neu-inset p-3" data-testid="organize-add">
      <ImagePlus className="h-4 w-4 text-[#3D3DF0]" />
      <span className="text-xs font-semibold">Add images or a PDF</span>
      <select value={pos} onChange={(e) => setPos(e.target.value)} className="rounded-full bg-white/70 px-3 py-1.5 text-xs focus:outline-none" data-testid="organize-add-position">
        <option value="0">At the beginning</option>
        {Array.from({ length: count }, (_, i) => <option key={i} value={i + 1}>{i + 1 === count ? `At the end (after page ${count})` : `After page ${i + 1}`}</option>)}
      </select>
      <button disabled={busy} onClick={() => input.current?.click()} className="neu-btn-primary flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold disabled:opacity-60" data-testid="organize-add-btn">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}Choose files
      </button>
      <input ref={input} type="file" multiple hidden accept="application/pdf,.pdf,image/*" onChange={(e) => onAdd(Array.from(e.target.files || []), Number(pos))} data-testid="organize-add-input" />
    </div>
  );
};

export const PagesDialog = ({ open, onOpenChange, book, beforeChange, onDone }) => {
  const [order, setOrder] = useState([]);
  const [removed, setRemoved] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const dragId = useRef(null);
  useEffect(() => { if (open) { setOrder(book.pages.map((p) => p.index)); setRemoved(new Set()); } }, [open, book.pages]);
  const byIndex = Object.fromEntries(book.pages.map((p) => [p.index, p]));
  const dirty = removed.size > 0 || order.some((v, i) => v !== book.pages[i]?.index);

  const drop = (target) => {
    const from = dragId.current;
    if (from == null || from === target) return;
    setOrder((o) => { const c = o.filter((x) => x !== from); c.splice(c.indexOf(target), 0, from); return c; });
  };
  const toggle = (i) => setRemoved((r) => { const n = new Set(r); n.has(i) ? n.delete(i) : n.add(i); return n; });

  const apply = async () => {
    const final = order.filter((i) => !removed.has(i));
    if (!final.length) return toast.error("A book needs at least one page");
    setBusy(true);
    try {
      await beforeChange();
      await api.put(`/books/${book.book_id}/pages/order`, { order: final });
      toast.success("Pages updated");
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const add = async (files, position) => {
    if (!files.length) return;
    const pdf = files.length === 1 && files[0].name.toLowerCase().endsWith(".pdf");
    if (!pdf && !files.every((f) => IMG.test(f.name))) return toast.error("Choose one PDF, or one or more images");
    setBusy(true);
    try {
      await beforeChange();
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("position", String(position));
      await api.post(`/books/${book.book_id}/pages/insert`, fd);
      toast.success(`Adding ${pdf ? "PDF pages" : `${files.length} image${files.length > 1 ? "s" : ""}`}…`);
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  let n = 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-hidden border-0 neu-panel" data-testid="organize-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Organize pages</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">Drag a page onto another to move it there. Hotspots and other layers move with their page.</DialogDescription>
        </DialogHeader>
        <AddPages count={book.pages.length} busy={busy} onAdd={add} />
        <div className="thin-scroll grid max-h-[48vh] grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-5 lg:grid-cols-6" data-testid="organize-grid">
          {order.map((i) => <Tile key={i} p={byIndex[i]} n={removed.has(i) ? 0 : ++n} removed={removed.has(i)} onDrop={drop} onToggle={toggle} dragId={dragId} />)}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="neu-btn rounded-full px-5 py-2 text-xs font-semibold" data-testid="organize-cancel">Cancel</button>
          <button disabled={!dirty || busy} onClick={apply} className="neu-btn-primary flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-bold disabled:opacity-50" data-testid="organize-save">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Save page order
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
